/**
 * The built-in column (defs/column.ts) as a data spec: the classic plan
 * symbol — the outline crossed by both diagonals. The `shape` choice picks
 * one of two groups (`visibleWhen`):
 *  - rectangular: the footprint box plus its two corner-to-corner diagonals;
 *  - round: the ellipse inscribed in the footprint (a 24-sided polygon, like
 *    the code) and the diagonals clipped to it at its 45° points.
 * Everything is authored at the default 0.3 × 0.3 m size and simply
 * stretches with the footprint — a stretched circle point `(r cos t, r sin t)`
 * lands on `(width/2 cos t, depth/2 sin t)`, exactly the code's ellipse.
 *
 * `columnHeight` is the vertical bound kept by the floor→layout conversion
 * (the code type's `height` param — `height` is a reserved name in specs, it
 * already means the object's live height). It does not affect the 2D glyph.
 */

import { OBJECT_DEFINITION_SCHEMA_VERSION, type ObjectDefinitionSpec, type Vec2 } from '../spec';

const SIZE = 0.3;
const H = SIZE / 2;
/** Sides of the round column's polygon. */
const ROUND_SEGMENTS = 24;
/** Where the diagonals meet the round outline (the 45° points). */
const C = H * Math.SQRT1_2;

const roundOutline: Vec2[] = Array.from({ length: ROUND_SEGMENTS }, (_, k): Vec2 => {
  const t = (2 * Math.PI * k) / ROUND_SEGMENTS;
  return [H * Math.cos(t), H * Math.sin(t)];
});

export const columnSpec: ObjectDefinitionSpec = {
  schemaVersion: OBJECT_DEFINITION_SCHEMA_VERSION,
  anchor: 'POINT',
  constraint: { kind: 'FREE' },
  defaultSize: { width: SIZE, depth: SIZE, height: 3 },
  params: [
    {
      key: 'shape',
      kind: 'select',
      label: 'shape',
      default: 'rectangular',
      options: [
        { value: 'rectangular', label: 'rectangular' },
        { value: 'round', label: 'round' },
      ],
    },
    { key: 'columnHeight', kind: 'float', label: 'height', default: 3.0, min: 0, decimals: 2, distance: true },
  ],
  shapes: [
    { id: 'box', name: 'Rectangular outline', kind: 'rect', min: [-H, -H], max: [H, H] },
    {
      id: 'boxDiagonalUp',
      name: 'First diagonal',
      kind: 'polyline',
      points: [
        [-H, -H],
        [H, H],
      ],
      closed: false,
    },
    {
      id: 'boxDiagonalDown',
      name: 'Second diagonal',
      kind: 'polyline',
      points: [
        [-H, H],
        [H, -H],
      ],
      closed: false,
    },
    { id: 'ring', name: 'Round outline', kind: 'polyline', points: roundOutline, closed: true },
    {
      id: 'ringDiagonalUp',
      name: 'First diagonal',
      kind: 'polyline',
      points: [
        [-C, -C],
        [C, C],
      ],
      closed: false,
    },
    {
      id: 'ringDiagonalDown',
      name: 'Second diagonal',
      kind: 'polyline',
      points: [
        [-C, C],
        [C, -C],
      ],
      closed: false,
    },
  ],
  groups: [
    {
      id: 'rectangular',
      name: 'Rectangular column',
      shapeIds: ['box', 'boxDiagonalUp', 'boxDiagonalDown'],
      resize: { u: 'stretch', n: 'stretch' },
      visibleWhen: { param: 'shape', equals: 'rectangular' },
    },
    {
      id: 'round',
      name: 'Round column',
      shapeIds: ['ring', 'ringDiagonalUp', 'ringDiagonalDown'],
      resize: { u: 'stretch', n: 'stretch' },
      visibleWhen: { param: 'shape', equals: 'round' },
    },
  ],
  model3d: null,
};
